/**
 * Reference-image entry point for /home (PromptView).
 *
 * Two-step flow:
 *   1. User drops or picks a photo. We pre-create a draft conversation
 *      doc, upload the image to that conversation's storage path, and
 *      then show a preview thumbnail with a "Generate 3D mesh" button.
 *   2. User clicks "Generate 3D mesh" → we call the `startMeshFromImage`
 *      Cloud Function and then `onMeshKickedOff` so PromptView can
 *      navigate into the editor. A "Clear" button resets to the empty
 *      state if the user wants to pick a different photo.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Image as ImageIcon, Loader2, Sparkles } from 'lucide-react';
import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { firestore, storage } from '@/lib/firebase';
import { getConversationImagePath } from '@/lib/firebaseAssetPaths';
import { useToast } from '@/hooks/use-toast';
import { startMeshFromImage } from '@/services/meshGenerationService';
import { cn } from '@/lib/utils';

const VALID_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 4 * 1024 * 1024;

export interface MeshKickoff {
  conversationId: string;
  taskId: string;
  imageId: string;
}

interface Props {
  /** UUID for the conversation that will be created on file pick. */
  conversationId: string;
  /** Logged-in user id; required to compute the storage path. */
  userId: string;
  /** Called after upload + mesh-job kickoff succeeds. */
  onMeshKickedOff: (kickoff: MeshKickoff) => void | Promise<void>;
  /** Locks the card while parent is starting the build (post-click). */
  isBuilding: boolean;
  /** Optional: called when the user clears the staged image. */
  onCleared?: () => void;
  /** When true, drag/drop and the buttons are disabled (parent is gating
   *  this card via the mesh-mode toggle). */
  disabled?: boolean;
}

export function ImageStartCard({
  conversationId,
  userId,
  onMeshKickedOff,
  isBuilding,
  onCleared,
  disabled = false,
}: Props) {
  const { t } = useTranslation('prompt');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragHover, setIsDragHover] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isKickingOff, setIsKickingOff] = useState(false);
  // After upload, we keep the imageId we wrote to Storage and a local
  // preview URL (object URL from the picked File) for the thumbnail.
  const [staged, setStaged] = useState<{
    imageId: string;
    previewUrl: string;
  } | null>(null);

  // Revoke the object URL when the staged image changes or the card unmounts.
  useEffect(() => {
    if (!staged) return;
    return () => URL.revokeObjectURL(staged.previewUrl);
  }, [staged]);

  const resetToEmpty = () => {
    setStaged(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onCleared?.();
  };

  const handleFile = async (file: File) => {
    if (isUploading || isKickingOff || isBuilding || disabled) return;
    if (!VALID_TYPES.includes(file.type.toLowerCase())) {
      toast({
        title: t('imageStart.invalidTypeTitle', {
          defaultValue: 'Unsupported image type',
        }),
        description: t('imageStart.invalidTypeDescription', {
          defaultValue: 'Please upload a JPEG, PNG, or WEBP file.',
        }),
        variant: 'destructive',
      });
      return;
    }
    if (file.size > MAX_BYTES) {
      toast({
        title: t('imageStart.tooLargeTitle', {
          defaultValue: 'Image too large',
        }),
        description: t('imageStart.tooLargeDescription', {
          defaultValue: 'Maximum size is 4 MB. Resize and try again.',
        }),
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    const imageId = crypto.randomUUID();
    let step: 'create-conversation' | 'upload-image' = 'create-conversation';
    try {
      // 1. Pre-create the conversation doc as a draft so the
      //    startMeshFromImage endpoint's auth check finds an owner
      //    record. PromptView later treats this same id as the
      //    conversation it navigates to.
      const convRef = doc(firestore, 'conversations', conversationId);
      const now = new Date().toISOString();
      await setDoc(convRef, {
        id: conversationId,
        user_id: userId,
        created_at: now,
        updated_at: now,
        current_message_leaf_id: null,
        draft: true,
      });

      // 2. Upload the image to the conversation-scoped storage path.
      step = 'upload-image';
      const path = getConversationImagePath(userId, conversationId, imageId);
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type });

      // 3. Stage the image — DO NOT call startMeshFromImage yet; the
      //    user has to confirm by clicking the "Generate 3D mesh" button.
      const previewUrl = URL.createObjectURL(file);
      setStaged({ imageId, previewUrl });
    } catch (err) {
      console.error(`[ImageStartCard] failed at step="${step}":`, err);
      const stepLabel =
        step === 'create-conversation'
          ? t('imageStart.steps.createConversation', {
              defaultValue: 'creating the draft conversation',
            })
          : t('imageStart.steps.uploadImage', {
              defaultValue: 'uploading the image',
            });
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast({
        title: t('imageStart.uploadFailedTitle', {
          defaultValue: "Couldn't upload image",
        }),
        description:
          errorMessage +
          ' ' +
          t('imageStart.kickoffFailedWhile', {
            step: stepLabel,
            defaultValue: '(while {{step}})',
          }),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!staged || isKickingOff || isBuilding || disabled) return;
    setIsKickingOff(true);
    try {
      const { taskId } = await startMeshFromImage({
        conversationId,
        imageId: staged.imageId,
      });
      await onMeshKickedOff({
        conversationId,
        taskId,
        imageId: staged.imageId,
      });
    } catch (err) {
      console.error('[ImageStartCard] kickoff failed:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      toast({
        title: t('imageStart.kickoffFailedTitle', {
          defaultValue: "Couldn't start mesh generation",
        }),
        description: errorMessage,
        variant: 'destructive',
      });
      setIsKickingOff(false);
    }
  };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    // Reset so re-selecting the same file fires onChange again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragHover(false);
    if (disabled || staged) return;
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const busy = isUploading || isKickingOff || isBuilding;

  // Two-step: once an image is staged, show the preview + confirm UI.
  if (staged) {
    return (
      <div className="flex w-full flex-col items-center gap-3 rounded-lg border-2 border-brand-indigo/30 bg-k-card p-4 text-sm">
        <div className="flex items-center gap-2 text-brand-indigo">
          <ImageIcon className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-wider">
            {t('imageStart.confirmHeadline', {
              defaultValue: 'Ready to generate?',
            })}
          </span>
        </div>
        <img
          src={staged.previewUrl}
          alt={t('imageStart.previewAlt', {
            defaultValue: 'Uploaded reference image preview',
          })}
          className="h-24 w-24 rounded-md border border-brand-indigo/20 object-cover"
        />
        <p className="text-center text-xs text-k-ink/70">
          {t('imageStart.subline', {
            defaultValue:
              'Drop a photo to generate a sculpted 3D mesh (not parametrically editable).',
          })}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetToEmpty}
            disabled={isKickingOff || isBuilding || disabled}
            className="inline-flex items-center justify-center rounded-full border-2 border-brand-indigo/40 bg-transparent px-3.5 py-1.5 text-xs font-bold text-brand-indigo transition-colors hover:border-brand-indigo hover:bg-brand-teal-soft/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('imageStart.clearButton', { defaultValue: 'Clear' })}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isKickingOff || isBuilding || disabled}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-brand-indigo bg-brand-indigo px-3.5 py-1.5 text-xs font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover disabled:cursor-wait disabled:opacity-60"
          >
            {isKickingOff || isBuilding ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            <span>
              {isKickingOff || isBuilding
                ? t('imageStart.starting', {
                    defaultValue: 'Starting mesh generation…',
                  })
                : t('imageStart.confirmButton', {
                    defaultValue: 'Generate 3D mesh',
                  })}
            </span>
            {!(isKickingOff || isBuilding) && (
              <ArrowRight className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>
    );
  }

  // Empty state: drop zone / picker.
  return (
    <div
      onDragEnter={(e) => {
        if (disabled) return;
        e.preventDefault();
        setIsDragHover(true);
      }}
      onDragOver={(e) => {
        if (disabled) return;
        e.preventDefault();
      }}
      onDragLeave={() => setIsDragHover(false)}
      onDrop={onDrop}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4 text-sm transition-colors',
        isDragHover
          ? 'border-brand-indigo bg-brand-teal-soft/60'
          : 'border-brand-indigo/30 bg-k-card hover:border-brand-indigo/60 hover:bg-brand-teal-soft/30',
      )}
    >
      <div className="flex items-center gap-2 text-brand-indigo">
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ImageIcon className="h-4 w-4" />
        )}
        <span className="text-xs font-bold uppercase tracking-wider">
          {busy
            ? t('imageStart.uploading', {
                defaultValue: 'Uploading image…',
              })
            : t('imageStart.headline', {
                defaultValue: 'Have a reference image?',
              })}
        </span>
      </div>
      <p className="text-center text-xs text-k-ink/70">
        {t('imageStart.subline', {
          defaultValue:
            'Drop a photo to generate a sculpted 3D mesh (not parametrically editable).',
        })}
      </p>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={busy || disabled}
        className="mt-1 inline-flex items-center gap-1.5 rounded-full border-2 border-brand-indigo bg-brand-indigo px-3.5 py-1.5 text-xs font-bold text-brand-cream transition-colors hover:bg-brand-indigo-hover disabled:cursor-wait disabled:opacity-60"
      >
        <Sparkles className="h-3.5 w-3.5" />
        {t('imageStart.pickButton', { defaultValue: 'Choose image' })}
        <ArrowRight className="h-3.5 w-3.5" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={VALID_TYPES.join(',')}
        className="hidden"
        onChange={onSelectFile}
        disabled={busy || disabled}
      />
    </div>
  );
}
